import { useEffect, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { UserPicker } from '../features/users/UserPicker';
import { gateableFunctions } from '../config/featureNav';
import type { AccessUserDto, DirectoryUserDto } from '../api/types';

/**
 * Feingranulare In-App-Berechtigungen — als Tab innerhalb der Einstellungen.
 * Der Admin-Schutz wird von der umgebenden Settings-Seite gestellt.
 */
export function AccessSettings() {
  const api = useApi();
  const state = useAsync(() => api.getAccessConfig(), [api]);
  const [rows, setRows] = useState<AccessUserDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) setRows(state.data);
  }, [state.data]);

  function addUser(u: DirectoryUserDto | undefined) {
    if (!u) return;
    setRows((rs) =>
      rs.some((r) => r.userObjectId === u.id)
        ? rs
        : [...rs, { userObjectId: u.id, userUpn: u.userPrincipalName, deniedPageKeys: [] }],
    );
  }

  async function toggle(row: AccessUserDto, pageKey: string, allowed: boolean) {
    const denied = allowed
      ? row.deniedPageKeys.filter((k) => k !== pageKey)
      : [...row.deniedPageKeys, pageKey];
    setRows((rs) => rs.map((r) => (r.userObjectId === row.userObjectId ? { ...r, deniedPageKeys: denied } : r)));
    try {
      await api.setUserAccess(row.userObjectId, { userUpn: row.userUpn, deniedPageKeys: denied });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(row: AccessUserDto) {
    try {
      await api.removeUserAccess(row.userObjectId);
      setRows((rs) => rs.filter((r) => r.userObjectId !== row.userObjectId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div style={{ maxWidth: '820px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        Feingranular festlegen, welche Funktionen einzelne Nutzer verwenden dürfen. Gesperrte Funktionen erscheinen beim
        Nutzer mit rotem Schloss in der Navigation.
      </Caption1>

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <Field label="Nutzer hinzufügen">
        <UserPicker onSelect={addUser} placeholder="Person suchen und hinzufügen…" clearOnSelect />
      </Field>

      {state.loading ? <Spinner size="tiny" label="Lade…" /> : null}
      {rows.length === 0 && !state.loading ? (
        <Body1>Noch keine Nutzer konfiguriert — standardmäßig dürfen alle berechtigten Nutzer alle Funktionen.</Body1>
      ) : null}

      {rows.map((row) => (
        <Card
          key={row.userObjectId}
          style={{ padding: tokens.spacingVerticalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text weight="semibold" className="pii">
              {row.userUpn}
            </Text>
            <Button size="small" appearance="subtle" icon={<DeleteRegular />} onClick={() => remove(row)}>
              Zurücksetzen
            </Button>
          </div>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Häkchen = erlaubt. Entfernen sperrt die Funktion für diesen Nutzer.
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalXS }}>
            {gateableFunctions.map((fn) => (
              <Checkbox
                key={fn.pageKey}
                label={fn.label}
                checked={!row.deniedPageKeys.includes(fn.pageKey)}
                onChange={(_, d) => toggle(row, fn.pageKey, Boolean(d.checked))}
                style={{ minWidth: '210px' }}
              />
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
